/**
 * Task-assignment people and coverage groups for the prototype.
 *
 * A message or task can be handed off to another driver manager or a coverage
 * group. The option list for a driver is ordered the way a dispatcher thinks
 * about escalation: the driver's own manager first, then the team lead, then
 * peers on the same team, then the standing coverage groups. All names are
 * fabricated demo data, deterministic per driver.
 */

import { hash } from "./hash";

export interface AssignmentOption {
  id: string;
  name: string;
  /** Relation/role to the driver, shown under the name. */
  role: string;
  kind: "person" | "group";
}

/** The signed-in dispatcher, used for "assigned to me" / "assigned by" copy. */
export const CURRENT_USER = "You";

const MANAGER_FIRST = [
  "Dana",
  "Greg",
  "Priya",
  "Hector",
  "Renee",
  "Curtis",
  "Selena",
  "Omar",
  "Gloria",
  "Travis",
  "Nadia",
  "Brett",
  "Imani",
  "Wesley",
  "Bianca",
  "Andre",
  "Latoya",
  "Cole",
  "Yusuf",
  "Rosa",
];

const MANAGER_LAST = [
  "Whitfield",
  "Coleman",
  "Nguyen",
  "Petrov",
  "Haddad",
  "Boone",
  "Greer",
  "Mathis",
  "Underwood",
  "Salazar",
  "Dunlap",
  "Beasley",
  "Maddox",
  "Crews",
  "Vaughn",
  "Holloway",
  "Reyes",
  "Foster",
  "Delgado",
  "Simmons",
];

function managerName(seed: string): string {
  return `${MANAGER_FIRST[hash(`mf::${seed}`) % MANAGER_FIRST.length]} ${MANAGER_LAST[hash(`ml::${seed}`) % MANAGER_LAST.length]}`;
}

/** The driver's assigned Driver Manager — also the "assigned by" for inbound hand-offs. */
export function assignedByFor(driverId: string): string {
  return managerName(`dm::${driverId}`);
}

/**
 * Ordered hand-off targets for a driver: their Driver Manager (pre-selected),
 * their Team Lead, three peers on the same team, then the coverage groups.
 *
 * @param driverId - The driver whose message/task is being assigned.
 */
export function assignmentOptionsFor(driverId: string): AssignmentOption[] {
  return [
    { id: `${driverId}:dm`, name: assignedByFor(driverId), role: "Driver Manager · assigned", kind: "person" },
    { id: `${driverId}:tl`, name: managerName(`tl::${driverId}`), role: "Team Lead", kind: "person" },
    {
      id: `${driverId}:peer1`,
      name: managerName(`p1::${driverId}`),
      role: "Driver Manager · same team",
      kind: "person",
    },
    {
      id: `${driverId}:peer2`,
      name: managerName(`p2::${driverId}`),
      role: "Driver Manager · same team",
      kind: "person",
    },
    {
      id: `${driverId}:peer3`,
      name: managerName(`p3::${driverId}`),
      role: "Driver Manager · same team",
      kind: "person",
    },
    { id: "group:overnight", name: "Continuing Coverage — Overnight", role: "Coverage group", kind: "group" },
    { id: "group:weekend", name: "Continuing Coverage — Weekend", role: "Coverage group", kind: "group" },
  ];
}
